'use client';

import { type FundingModel, FundingType } from '@bytel/leviator';
import {
    DirectionEnum,
    FlexBox,
    FlexItem,
    Icon,
    IconName,
    Popover,
    RadioTile,
    Text,
    TextLevels,
    TypographyBold,
} from '@bytel/trilogy-react';

import { InlinePrice } from '@components/inline-price';
import { YounitedIcon } from '@components/younited';

import { getFundingDescription } from '@helpers/funding';
import { formatPrice } from '@helpers/wording';

import { OdrInfos } from './odr-infos';

interface FundingOptionItemProps {
    funding: FundingModel;
    fundingOptions: FundingModel[];
    isSelected: boolean;
    onSelect: () => void;
    hasOdr: boolean;
    odrRefund: number;
    phonePriceWithOdrDeducted: number;
}

export function FundingOptionItem({
    funding,
    fundingOptions,
    isSelected,
    onSelect,
    hasOdr,
    odrRefund,
    phonePriceWithOdrDeducted,
}: Readonly<FundingOptionItemProps>) {
    const isYounited = funding.isType(FundingType.YOUNITED) || funding.isType(FundingType.SWAPPY);
    const isEdp = funding.isType(FundingType.EDP);
    const isComptant = funding.isType(FundingType.COMPTANT);
    const description = getFundingDescription(funding, fundingOptions);

    const hasMultipleKeys = fundingOptions.length > 1;
    const nonComptantAmount = hasMultipleKeys
        ? Math.min(...fundingOptions.map((f) => f.initialDeposit))
        : funding.initialDeposit;
    const displayAmount = isComptant ? funding.totalFinancingCost : nonComptantAmount;

    return (
        <RadioTile
            id={funding.type}
            horizontal
            name='paymentMethod'
            checked={isSelected}
            onChange={onSelect}
            description={
                <FlexBox direction={DirectionEnum.COLUMN} gap={3}>
                    {isYounited && (
                        <FlexItem narrow>
                            <YounitedIcon />
                        </FlexItem>
                    )}
                    <FlexItem>
                        <Text level={TextLevels.ONE} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                            {hasMultipleKeys && 'À partir de '}
                            <InlinePrice amount={displayAmount} level={TextLevels.ONE} />
                            {isEdp && funding.amountToFinance > 0 && (
                                <>
                                    {' '}
                                    <Popover trigger={<Icon name={IconName.INFOS_CIRCLE} />}>
                                        Facilité de paiement du téléphone à hauteur de{' '}
                                        {formatPrice(funding.amountToFinance)}. Le restant dû étant à payer au comptant.
                                    </Popover>
                                </>
                            )}
                        </Text>
                        {description && <Text level={TextLevels.TWO}>{description}</Text>}
                        {hasOdr && isSelected && (isComptant || isEdp) && (
                            <OdrInfos odrRefund={odrRefund} phonePriceWithOdrDeducted={phonePriceWithOdrDeducted} />
                        )}
                    </FlexItem>
                </FlexBox>
            }
        />
    );
}
